import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector, signal, type Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';
import { ResourceTypes, RestApiClientService } from '@big-direkt/rest-api-client';
import { EnvironmentService, ErrorHandlingService, FeatureFlagsService } from '@big-direkt/utils/environment';
import { firstValueFrom, of, tap, type Observable } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { type BonusMeasures } from '../models/bonus-measures.model';
import { type UserConsentIdEnum } from '../models/user-consent-ids.enum';
import { type UserProfileModel } from '../models/user-profile.model';
import { UserRoleModel } from '../models/user-roles.type';
import { AuthenticationStrategy } from '../strategies/authentication-strategy.interface';
import { ClassicAuthenticationStrategy } from '../strategies/classic-authentication.strategy';
import { NoopAuthenticationStrategy } from '../strategies/noop-authentication.strategy';
import { OAuthAuthenticationStrategy } from '../strategies/oauth-authentication.strategy';
import { type UserConsent } from './../models/user-consent.model';

interface ProfileData {
    withCatalogs: Observable<UserProfileModel | undefined> | undefined;
    withoutCatalogs: Observable<UserProfileModel | undefined> | undefined;
}

// TODO: create own service for autologout handling and info modal in BIGRED-858
@Injectable({ providedIn: 'root' })
export class UserService {
    private readonly featureFlagService = inject(FeatureFlagsService);
    private readonly restApiClient = inject(RestApiClientService);
    private readonly errorHandlingService = inject(ErrorHandlingService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly router = inject(Router);
    private readonly injector = inject(Injector);
    private readonly classicAuthStrategy = inject(ClassicAuthenticationStrategy);
    private readonly oauthAuthStrategy = inject(OAuthAuthenticationStrategy);

    public readonly isLoadingUserConsent = signal<boolean>(false);
    public readonly userConsentById = signal<UserConsent | undefined>(undefined);

    private initialization: Promise<void> | undefined;
    private authStrategy: AuthenticationStrategy = inject(NoopAuthenticationStrategy);
    private profileData: ProfileData = { withCatalogs: undefined, withoutCatalogs: undefined };

    public get isAuthenticated(): Signal<boolean> {
        return this.authStrategy.isAuthenticated;
    }

    public getToken(): string | undefined {
        return this.authStrategy.getToken();
    }

    public async initializeAuthentication(): Promise<void> {
        if (!this.initialization) {
            await this.featureFlagService.initUnleash(); // Ensure it is initialized before accessing
            this.authStrategy = this.featureFlagService.isFeatureEnabled('iam')() ? this.oauthAuthStrategy : this.classicAuthStrategy
            this.initialization = this.authStrategy.setup();
        }

        return this.initialization;
    }

    public getProfile(withCatalogs = false, useCache = true): Observable<UserProfileModel | undefined> {
        const isOGS2 = this.featureFlagService.isFeatureEnabled('ogs-2')();

        const key = withCatalogs ? 'withCatalogs' : 'withoutCatalogs';

        if (!this.profileData[key] || !useCache) {
            this.profileData[key] = toObservable(this.authStrategy.isAuthenticated, { injector: this.injector }).pipe(
                switchMap(isLoggedIn => {
                    if (!isLoggedIn) {
                        return of(undefined);
                    }

                    const request = isOGS2 ? this.loadUserProfileOGS2(withCatalogs) : this.loadUserProfileLegacy(withCatalogs);

                    return request.pipe(
                        shareReplay({
                            bufferSize: 1,
                            refCount: true,
                        }),
                    );
                }),
                shareReplay({
                    bufferSize: 1,
                    refCount: true,
                }),
            );
        }

        return this.profileData[key];
    }

    public getBonusMeasures(year: number | undefined): Observable<BonusMeasures> {
        const options = {
            params: {
                cb: Date.now(),
                ...(year !== undefined && { ...{ year } }),
            },
        };

        return this.restApiClient.load<BonusMeasures>(ResourceTypes.BonusMeasures, undefined, options);
    }

    public async logout(): Promise<void> {
        await this.authStrategy.logout();
        this.profileData = { withCatalogs: undefined, withoutCatalogs: undefined };
    }

    public login(destination?: string): void {
        this.authStrategy.login(destination);
    }

    public changePassword(destination?: string): void {
        this.authStrategy.changePassword(destination);
    }

    public getLoginUrl(destination?: string): UrlTree {
        let loginUrl = this.environmentService.user.loginRoute;
        const { logoutRoute } = this.environmentService.user;

        let cleanDestination = destination;
        if (logoutRoute && destination?.startsWith(logoutRoute)) {
            cleanDestination = this.environmentService.privatePath;
        }

        if (cleanDestination) {
            loginUrl += `?destination=${cleanDestination}`;
        }

        return this.router.parseUrl(loginUrl);
    }

    public getLogoutUrl(): UrlTree {
        return this.router.parseUrl(this.environmentService.user.logoutRoute);
    }

    public updateUserConsentById(id: UserConsentIdEnum): void {
        this.isLoadingUserConsent.set(true);

        void firstValueFrom(
            this.restApiClient
                .load<UserConsent | undefined>(ResourceTypes.UserConsent, undefined, {
                    params: {
                        cb: Date.now(),
                        id,
                    },
                })
                .pipe(
                    tap(res => {
                        this.userConsentById.set(res);
                        this.isLoadingUserConsent.set(false);
                    }),
                    catchError(err => {
                        this.isLoadingUserConsent.set(false);
                        this.errorHandlingService.catchError(err);

                        return of(undefined);
                    }),
                ),
        );
    }

    public hasRole(roleToCheck: UserRoleModel[] | UserRoleModel): Observable<boolean | undefined> {
        const safeguardedRole = !Array.isArray(roleToCheck) ? [roleToCheck] : roleToCheck;

        return this.getProfile().pipe(map(profile => safeguardedRole.some(r => profile?.roles.includes(r))));
    }

    public getCustomerSegment(): Observable<string | undefined> {
        return this.getProfile().pipe(map(profile => profile?.customerSegment));
    }

    private loadUserProfileLegacy(withCatalogs: boolean): Observable<UserProfileModel | undefined> {
        return this.restApiClient.load<UserProfileModel>(ResourceTypes.UserProfile, undefined, {
            params: {
                cb: Date.now(),
                // eslint-disable-next-line @typescript-eslint/naming-convention
                with_catalogs: withCatalogs,
            },
        });
    }

    private loadUserProfileOGS2(withCatalogs: boolean): Observable<UserProfileModel | undefined> {
        return this.restApiClient.load<UserProfileModel>(ResourceTypes.UserProfileDPP, undefined, {
            // TODO: global header for all requests to the API
            headers: new HttpHeaders({
                'Content-Type': 'application/vnd.api+json', // eslint-disable-line @typescript-eslint/naming-convention
            }),
            params: {
                cb: Date.now(),
                // eslint-disable-next-line @typescript-eslint/naming-convention
                with_catalogs: withCatalogs,
            },
        });
    }
}
